// Colormap-domain intent handlers.
//
// Owns: colormap name, range, units, center-on-zero, and the REFRESH_COLORMAP
// fan-out that re-applies the colormap to slices, volume, contours, and the
// colorbar HUD (see @app/colormapSync).
//
// Stage 2: also owns the colormap:create|update|remove|share|setVisible
// intents for the ColormapBinding registry. Legacy global SET_COLORMAP_*
// handlers stay as a translation layer — they propagate edits to the
// active binding so the render path (still reading legacy globals during
// Tasks 1-2) and the new binding shape stay coherent. Tasks 3-4 cut the
// legacy globals once the render side reads from bindings exclusively.

import { INTENTS } from '@store/intents';
import { params } from '@store/instance';
import { syncColormap } from '@app/colormapSync';
import {
    createBinding,
    findBindingByField,
    gcUnreferencedBindings,
    primitiveIdKey,
    removeBindingById,
    repointPrimitive,
    updateBindingFields,
} from '@store/bindings';
import type { ColormapBindingId, PrimitiveKey } from '@store/schema';
import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ViewerContext } from '../context';

/** All bindings touched by a legacy global update — those referenced by any primitive. */
function activeBindings(): ColormapBindingId[] {
    const ids = new Set<ColormapBindingId>();
    for (const k of ['slicesColormapId', 'volumeColormapId', 'contourColormapId', 'fieldlinesColormapId'] as const) {
        const v = params[k];
        if (v) ids.add(v);
    }
    return [...ids];
}

export function register(store: Store<State>, viewer: ViewerContext): Array<() => void> {
    const offs: Array<() => void> = [];
    const refresh = (): void => syncColormap(viewer, params);

    // --- Legacy global colormap intents (translation layer). ---
    // Each one writes the legacy param AND mirrors the change onto every
    // active binding. UI keeps dispatching these until Task 4 migrates
    // call sites to colormap:update.

    offs.push(store.onIntent(INTENTS.SET_COLORMAP, (p) => {
        if (!p || typeof p.name !== 'string') return;
        params.colormap = p.name;
        for (const id of activeBindings()) updateBindingFields(id, { colormap: p.name });
        refresh();
    }));

    offs.push(store.onIntent(INTENTS.SET_COLORMAP_RANGE, (p) => {
        if (!p) return;
        const patch: { min?: number; max?: number } = {};
        if (typeof p.min === 'number') { params.colormapMin = p.min; patch.min = p.min; }
        if (typeof p.max === 'number') { params.colormapMax = p.max; patch.max = p.max; }
        for (const id of activeBindings()) updateBindingFields(id, patch);
        refresh();
    }));

    offs.push(store.onIntent(INTENTS.SET_COLORMAP_UNITS, (p) => {
        if (!p || typeof p.units !== 'string') return;
        params.colormapUnits = p.units;
        for (const id of activeBindings()) updateBindingFields(id, { units: p.units });
    }));

    offs.push(store.onIntent(INTENTS.SET_CENTER_ON_ZERO, (p) => {
        if (!p) return;
        params.centerOnZero = !!p.value;
        for (const id of activeBindings()) updateBindingFields(id, { centerOnZero: !!p.value });
    }));

    offs.push(store.onIntent(INTENTS.SET_COLORMAP_LOG, (p) => {
        if (!p) return;
        params.colormapLog = !!p.value;
        for (const id of activeBindings()) updateBindingFields(id, { log: !!p.value });
        refresh();
    }));

    offs.push(store.onIntent(INTENTS.REFRESH_COLORMAP, () => refresh()));

    // --- Stage 2: binding-scoped intents. ---

    offs.push(store.onIntent(INTENTS.COLORMAP_CREATE, (p) => {
        if (!p || typeof p.field !== 'string') return;
        const opts: Parameters<typeof createBinding>[0] = { field: p.field };
        if (p.seed) opts.seed = p.seed;
        if (p.bindTo) opts.bindTo = p.bindTo;
        createBinding(opts);
        refresh();
    }));

    offs.push(store.onIntent(INTENTS.COLORMAP_UPDATE, (p) => {
        if (!p || !p.id || !p.patch) return;
        updateBindingFields(p.id, p.patch);
        // Translation: if the active LUT for this binding is also still
        // mirrored on the legacy globals (any primitive references it),
        // bring them along so older render paths stay coherent until Task 3.
        if (activeBindings().includes(p.id)) {
            if (typeof p.patch.colormap === 'string') params.colormap = p.patch.colormap;
            if (typeof p.patch.min === 'number') params.colormapMin = p.patch.min;
            if (typeof p.patch.max === 'number') params.colormapMax = p.patch.max;
            if (typeof p.patch.log === 'boolean') params.colormapLog = p.patch.log;
            if (typeof p.patch.units === 'string') params.colormapUnits = p.patch.units;
            if (typeof p.patch.centerOnZero === 'boolean') params.centerOnZero = p.patch.centerOnZero;
            if (typeof p.patch.transparentBounds === 'boolean') params.transparentBounds = p.patch.transparentBounds;
        }
        refresh();
    }));

    offs.push(store.onIntent(INTENTS.COLORMAP_REMOVE, (p) => {
        if (!p || !p.id) return;
        removeBindingById(p.id);
        refresh();
    }));

    offs.push(store.onIntent(INTENTS.COLORMAP_SHARE, (p) => {
        if (!p || !p.primitive || !p.targetId) return;
        if (!params.bindings[p.targetId]) return;
        repointPrimitive(p.primitive as PrimitiveKey, p.targetId);
        gcUnreferencedBindings();
        // Mirror the target's display state onto legacy globals so any
        // not-yet-migrated render code still draws correctly.
        const b = params.bindings[p.targetId];
        if (b) {
            params.colormap = b.colormap;
            params.colormapMin = b.min;
            params.colormapMax = b.max;
            params.colormapLog = b.log;
            params.colormapUnits = b.units;
            params.centerOnZero = b.centerOnZero;
            params.transparentBounds = b.transparentBounds;
        }
        refresh();
    }));

    offs.push(store.onIntent(INTENTS.COLORMAP_SET_VISIBLE, (p) => {
        if (!p || !Array.isArray(p.ids)) return;
        const valid = p.ids.filter((id) => !!params.bindings[id]);
        params.visibleColorbarIds = valid;
    }));

    // Units toggle (stub). Flips a label prefix in the colorbar; the
    // numeric multiplier wires up once normalization metadata lands.
    offs.push(store.onIntent(INTENTS.SET_COLORBAR_UNITS, (p) => {
        if (!p || (p.mode !== 'native' && p.mode !== 'code')) return;
        params.colorbarUnits = p.mode;
        refresh();
    }));

    return offs;
}

// Re-export so unit tests can poke the helper without dragging the whole
// register() factory in.
export { primitiveIdKey, findBindingByField };
